import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { handleAuthentication, showSnackBar } from "@containers/App/reducer"
import { parseWidgets, parseYourPrescriptions, parseUploadLimit, parseMemberListData } from "./parser"

const initialState = {
    isLoading: false,
    isFetched: false,
    error: null,
    widgets: {},
    yourPrescriptions: {},
    uploadLimit: { message: "", limit: null, totalLimit: null },
    isUploading: false,
    hasMore: false,
    currentPageNumber: 0,
    isFetchedMore: false,
    isFetchMoreError: false,
    isFetchingMore: false,
    uploadFileData: null,
    isMemberListDataFetched: false,
    memberListData: null,
}

const getDataFromXml = (xmlData, key) => {
    let parser = new DOMParser()
    let xmlDoc = parser.parseFromString(xmlData, "text/xml")
    return xmlDoc.getElementsByTagName(key)[0].childNodes[0].nodeValue
}

export const dioAttachPrescriptionSlice = createSlice({
    name: "dioPrescriptionAttachPage",
    initialState: initialState,
    reducers: {
        reset: () => initialState,
        setIsUploading: (state, action) => {
            state.isUploading = action.payload
        },
        setUploadFileData: (state, action) => {
            state.uploadFileData = action.payload
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchDioPrescriptionData.fulfilled, (state, action) => {
            state.isLoading = false
            state.isFetched = true
            state.error = false
            state.widgets = parseWidgets(action.payload)
            state.uploadLimit = parseUploadLimit(action.payload)
        }),
            builder.addCase(fetchDioPrescriptionData.pending, (state) => {
                state.isLoading = true
                state.isFetched = false
                state.error = false
            }),
            builder.addCase(fetchDioPrescriptionData.rejected, (state) => {
                state.isLoading = false
                state.error = true
                state.isFetched = false
            }),
            builder.addCase(fetchYourPrescriptions.fulfilled, (state, action) => {
                const data = parseYourPrescriptions(action.payload?.response, action.payload?.pageMeta)
                state.yourPrescriptions = data?.yourPrescriptions
                state.currentPageNumber = data?.currentPageNumber
                state.hasMore = data?.hasMore
            }),
            builder.addCase(fetchMorePrescriptions.fulfilled, (state, action) => {
                state.isFetchedMore = true
                state.isFetchMoreError = false
                state.isFetchingMore = false
                const data = parseYourPrescriptions(
                    action?.payload?.response,
                    action.payload?.pageMeta,
                    action?.payload?.pageNumber,
                    action?.payload?.existingPrescriptions,
                    action?.payload?.existingSelectedImages
                )
                state.yourPrescriptions = data?.yourPrescriptions
                state.currentPageNumber = data?.currentPageNumber
                state.hasMore = data?.hasMore
            }),
            builder.addCase(fetchMorePrescriptions.pending, (state) => {
                state.isFetchedMore = false
                state.isFetchMoreError = false
                state.isFetchingMore = true
            }),
            builder.addCase(fetchMorePrescriptions.rejected, (state) => {
                state.isFetchedMore = false
                state.isFetchMoreError = true
                state.isFetchingMore = false
            })
        builder.addCase(fetchPatientRxListData.pending, (state) => {
            state.isLoading = true
            state.isMemberListDataFetched = false
            state.error = false
        })
        builder.addCase(fetchPatientRxListData.fulfilled, (state, action) => {
            state.isLoading = false
            state.isMemberListDataFetched = true
            state.memberListData = parseMemberListData(action.payload)
            state.error = false
        })
    },
})

export const fetchDioPrescriptionData = createAsyncThunk(
    "dioAttachPrescriptionSlice/fetchPrescriptionPageData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const successCallback = _arg?.successCallback
        try {
            const res = await api.get(`/api/v4/static/upload_rx_info_screen`)
            if (typeof successCallback === "function") {
                successCallback(res?.data)
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const errorCallback = _arg?.errorCallback
            if (typeof errorCallback === "function") {
                errorCallback()
            }
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const fetchYourPrescriptions = createAsyncThunk(
    "dioAttachPrescriptionSlice/fetchYourPrescriptions",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { type, pageNumber, pageSize } = _arg
        try {
            const res = await api.get(
                `api/v4/prescriptions/all?types=${type}&page_number=${pageNumber}&page_size=${pageSize}`
            )
            return Promise.resolve({
                response: res.data,
                pageMeta: res.meta,
            })
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const fetchMorePrescriptions = createAsyncThunk(
    "dioAttachPrescriptionSlice/fetchMorePrescriptions",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        const { type, pageNumber, pageSize, existingPrescriptions, existingSelectedImages } = _arg
        try {
            const res = await api.get(
                `api/v4/prescriptions/all?types=${type}&page_number=${pageNumber}&page_size=${pageSize}`
            )
            return Promise.resolve({
                response: res.data,
                pageNumber: pageNumber,
                existingPrescriptions: existingPrescriptions,
                existingSelectedImages: existingSelectedImages,
                pageMeta: res.meta,
            })
        } catch (err) {
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const attachPrescription = createAsyncThunk(
    "dioAttachPrescriptionSlice/attachPrescription",
    async ({ payload, successCallback, errorCallback }, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        try {
            const res = await api.put(`api/v4/carts/prescriptions?types=done_in_one`, payload)
            successCallback?.(res.data)
            return Promise.resolve(res.data)
        } catch (err) {
            errorCallback?.()
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 401 || err.status_code === 403))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const finishCart = createAsyncThunk(
    "dioAttachPrescriptionSlice/finishPreviousCart",
    async ({ successCallback, errorCallback }, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        try {
            const res = await api.post(`/api/v4/finish_cart`, { types: "DONE_IN_ONE" })
            successCallback?.()
            return Promise.resolve(res.data)
        } catch (err) {
            errorCallback?.()
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 401 || err.status_code === 403))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)

export const removePrescription = createAsyncThunk(
    "dioAttachPrescriptionSlice/removePrescription",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra

        const id = _arg?.id
        const type = _arg?.type
        const successCallback = _arg?.successCallback
        try {
            const res = await api.delete(
                `/webservices/orders/current/prescriptions/remove-multiple/${id}?cart_type=${type}`
            )
            if (typeof successCallback === "function") {
                successCallback()
            }
            return Promise.resolve(res.data)
        } catch (err) {
            const errorCallback = _arg?.errorCallback
            if (typeof errorCallback === "function") {
                errorCallback()
            }
            const onServer = typeof window === "undefined"
            if (err?.status_code !== 403 || err?.status_code !== 401) {
                showSnackBar(dispatch, err.error.message)
            }
            if (!onServer && (err.status_code === 403 || err.status_code === 401))
                dispatch(handleAuthentication())
            return rejectWithValue(err)
        }
    }
)
export const uploadPrescription = createAsyncThunk(
    "dioAttachPrescriptionSlice/uploadPrescription",
    async ({ payload, file, errorCallback }, { extra, rejectWithValue, dispatch }) => {
        const { api } = extra
        try {
            return await api
                .post(`/api/v4/presigned_urls`, payload)
                .then((response) => {
                    if (response?.data?.image4upload?.fields) {
                        const formData = new FormData()
                        for (const key in response.data.image4upload.fields) {
                            formData.append(key, response.data.image4upload.fields[key])
                        }
                        formData.append("file", file)
                        // Api call to aws for image upload
                        return axios
                            .post(response?.data?.image4upload.url, formData)
                            .then((response) => {
                                const responseUrl = getDataFromXml(response.data, "Location")
                                return Promise.resolve(responseUrl)
                            })
                            .catch((err) => {
                                if (typeof errorCallback === "function") {
                                    errorCallback()
                                }
                                showSnackBar(dispatch, "Something went wrong. Please try that again!")
                                return Promise.reject(err)
                            })
                    }
                })
                .catch((err) => {
                    return Promise.reject(err)
                })
        } catch (error) {
            if (typeof errorCallback === "function") {
                errorCallback()
            }
            if (error.status_code === 401 || error.status_code === 403) {
                dispatch(handleAuthentication())
            } else if (error.status_code === 400) {
                showSnackBar(dispatch, "Something went wrong. Please try that again!")
            } else {
                showSnackBar(dispatch, "Something went wrong. Please try that again!")
            }
            return rejectWithValue(error)
        }
    }
)

export const fetchPatientRxListData = createAsyncThunk(
    "dioAttachPrescriptionSlice/fetchPatientRxListData",
    async (_arg, { extra, rejectWithValue, dispatch, getState }) => {
        const { api } = extra
        const successCallback = _arg?.successCallback
        const corporateDetails = getState().shellReducer?.corporateDetails
        const corporateName = corporateDetails?._source
        try {
            const res = await api.get(`api/corporate/${corporateName}/dio/get_rx_listing_widgets`)
            if (typeof successCallback === "function") {
                successCallback(parseMemberListData(res.data))
            }
            return Promise.resolve(res.data)
        } catch (error) {
            const errorCallback = _arg?.errorCallback
            if (typeof errorCallback === "function") {
                errorCallback()
            }
            showSnackBar(dispatch, error.message)
            return rejectWithValue(error)
        }
    }
)

export const dioAttachPrescriptionReducer = dioAttachPrescriptionSlice.reducer
export const { reset, setIsUploading, setUploadFileData } = dioAttachPrescriptionSlice.actions
